import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { timeline } from '../mock';
import SectionLabel from '../components/SectionLabel';
import { Award, TrendingUp, ArrowRight, Loader2 } from 'lucide-react';
import { fetchCaseStudies } from '../lib/api';

const Experience = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies()
      .then(d => { setItems(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="pt-20 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1320px] mx-auto px-6">
          <SectionLabel icon={Award}>OPERATIONAL TRACK RECORD</SectionLabel>
          <h1 className="mt-4 font-serif text-white text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Field-tested delivery across state road authorities and Tier-1 consultancies.
          </h1>
          <p className="mt-5 max-w-2xl text-slate-400 text-lg leading-relaxed">
            Selected background write-ups from multi-year infrastructure delivery engagements across Australia.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1320px] mx-auto px-6">
          <SectionLabel icon={TrendingUp}>PROFESSIONAL TIMELINE</SectionLabel>
          <div className="mt-8 relative">
            <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-orange-400/40 to-transparent" />
            <div className="space-y-10">
              {timeline.map((t, i) => (
                <motion.div key={t.year}
                  initial={{ opacity: 0, x: i % 2 ? 20 : -20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6 }}
                  className={`relative pl-10 md:pl-0 md:grid md:grid-cols-2 md:gap-10 ${i % 2 ? 'md:direction-rtl' : ''}`}>
                  <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-1 w-6 h-6 rounded-full bg-orange-500/20 border border-orange-400/50 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-orange-400" />
                  </div>
                  <div className={`${i % 2 ? 'md:col-start-2' : ''}`}>
                    <div className="font-mono text-xs tracking-[0.2em] text-orange-300">{t.year}</div>
                    <div className="mt-1 font-serif text-white text-xl">{t.title}</div>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-md">{t.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1320px] mx-auto px-6 space-y-6">
          <SectionLabel>BACKGROUND WRITE-UPS</SectionLabel>
          {loading && <div className="text-sm text-slate-400 inline-flex items-center gap-2 mt-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            {items.map((w, i) => (
              <motion.div key={w.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                className="rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-6 hover-ring transition-all group">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm text-orange-300">{w.title}</div>
                  <div className="text-[10px] font-mono tracking-wider text-orange-300 px-2 py-1 rounded bg-orange-500/10 border border-orange-500/20">{w.tag}</div>
                </div>
                <div className="mt-4">
                  <div className="text-[11px] font-mono tracking-[0.2em] text-slate-500">BACKGROUND</div>
                  <p className="mt-1.5 text-sm text-slate-300 leading-relaxed line-clamp-3">{w.background}</p>
                </div>
                <div className="mt-4 rounded-lg border-l-2 border-emerald-400/60 bg-emerald-400/[0.04] p-3">
                  <div className="text-[11px] font-mono tracking-[0.2em] text-emerald-300">RESULT</div>
                  <p className="mt-1.5 text-sm text-slate-200 leading-relaxed line-clamp-3">{w.result}</p>
                </div>
                <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-end">
                  <Link to={`/case-study/${w.id}`} className="inline-flex items-center gap-1.5 text-sm text-orange-300 hover:text-orange-200 font-medium">
                    View full case study <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Experience;
